import { createSelector, MemoizedSelector } from '@ngrx/store';

import { Amount, SeatCharacteristics, ServiceCatalogSelections, TravelerService } from '@fcom/dapi/interfaces';
import {
  seatSelections,
  seatBundleCategoriesToSubCategoriesMappingSelector,
  serviceSelections,
  serviceSelectionsForCategory,
  seatBundleCategoriesSelector,
} from '@fcom/common-booking/store';
import { Category } from '@fcom/dapi/api/models';
import { sumAmounts } from '@fcom/common-booking/utils/amount.utils';

import { CommonBookingFeatureState } from '../store.interface';

/**
 * Returns the cost of all selected seats, excluding the ones that belong to bundles (no seatNumber), are included
 * in tickets, have already been paid, or for some other reason shouldn't be included in the ancillary subtotal flows.
 */
const nonBundledSeatSubTotalForJourneySelector = createSelector(seatSelections, (seatSelectionsByFlightAndPax) =>
  Object.values(seatSelectionsByFlightAndPax || {})
    .flat()
    .filter(seatServiceShouldCountTowardsSubTotal)
    .map((s) => multiplyAmount(s.pricePerItem, s.quantity))
    .reduce(sumAmounts, null)
);

/**
 * Returns the cost of all selected seat bundles of the category type. Not including standalone seats or items that
 * are included in the ticket, are missing seatNumber, are missing price information, etc.
 */
const seatBundleAncillarySubTotalForJourneySelector = (category: Category) =>
  createSelector(
    serviceSelections,
    seatBundleCategoriesToSubCategoriesMappingSelector,
    (selections, seatBundleCategories) => {
      const relevantSubCategories = seatBundleCategories?.[category];
      if (!relevantSubCategories || relevantSubCategories.size === 0) {
        return null;
      }
      const allSelections: TravelerService[] = Array.from(relevantSubCategories).reduce(
        (acc, cur) => [...acc, ...extractAllTravelerServicesFromSelection(selections?.[cur])],
        []
      );
      return allSelections
        .filter(seatServiceShouldCountTowardsSubTotal)
        .map((s) => multiplyAmount(s.pricePerItem, s.quantity))
        .reduce(sumAmounts, null);
    }
  );

/**
 * Returns the amount to be shown in a subtotal when the user is in some ancillary subflow of a given category.
 * Breaks down bundles into subcategories, determines which bundles are relevant for which subflows, excludes selected
 * services that have been paid or are included in the tickets, etc.
 */
export const nonSeatAncillarySubTotalForJourneySelector = (
  category: Category
): MemoizedSelector<CommonBookingFeatureState, Amount | null> =>
  createSelector(
    seatBundleCategoriesSelector,
    nonBundledSeatSubTotalForJourneySelector,
    seatBundleAncillarySubTotalForJourneySelector(category),
    serviceSelectionsForCategory<TravelerService[]>(category),
    (seatBundleCategories, nonBundledSeatsSubTotal, seatBundlesSubTotal, otherServiceSelections) => {
      if (category === Category.SEAT) {
        return nonBundledSeatsSubTotal;
      } else if (seatBundleCategories.has(category)) {
        return {
          currencyCode: nonBundledSeatsSubTotal?.currencyCode || seatBundlesSubTotal?.currencyCode,
          amount: String(Number(nonBundledSeatsSubTotal?.amount || '0') + Number(seatBundlesSubTotal?.amount || '0')),
        };
      } else {
        return extractAllTravelerServicesFromSelection(otherServiceSelections)
          .filter(nonSeatServiceShouldCountTowardsSubTotal)
          .map((s) => multiplyAmount(s.pricePerItem, s.quantity))
          .reduce(sumAmounts, null);
      }
    }
  );

/**
 * Extracts the individual services from the data structure that maps them by flight and passenger id.
 */
const extractAllTravelerServicesFromSelection = (
  seatSelectionsByFlightAndPax: ServiceCatalogSelections<TravelerService[]>
): TravelerService[] =>
  Object.values(seatSelectionsByFlightAndPax || {})
    .flatMap((v) => Object.values(v))
    .flat(2);

/**
 * As nonSeatServiceShouldCountTowardsSubTotal but also requires seatNumber
 */
const seatServiceShouldCountTowardsSubTotal = (service: TravelerService) =>
  !!service?.seatNumber &&
  !service.seatCharacteristics?.some((s) => s === SeatCharacteristics.SYSTEM) &&
  nonSeatServiceShouldCountTowardsSubTotal(service);

/**
 * Filters out services which are included in ticket, have already been paid, lack price info, etc.
 */
const nonSeatServiceShouldCountTowardsSubTotal = (service: TravelerService) =>
  service &&
  service.quantity > 0 &&
  !service.includedInTicketType &&
  !service.includedInTierBenefit &&
  !!service.pricePerItem?.currencyCode &&
  !service.paid;

/**
 * Multiply amount by quantity
 */
const multiplyAmount = (amount: Amount, quantity: number): Amount => {
  if (quantity === 1) {
    return amount;
  }
  return {
    ...amount,
    amount: String(Number(amount?.amount || '0') * quantity),
  };
};
